import axios, { type AxiosError } from 'axios';
import { getConfig } from '../config/config-helper';
import type {
  ISnowflakeAccount,
  IManagedKeyData,
  IAccountValidationResult,
  ICreateManagedKeyResponse,
  IConfirmManagedKeyResponse,
} from '../interfaces/ISnowflakeAccountManagement';

/**
 * Extract user-friendly error message from API error response
 */
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string; message?: string; error?: string } | string>;

    // Network errors (no response)
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return 'Request timed out. Please check your internet connection and try again.';
      }
      if (axiosError.message.includes('Network Error')) {
        return 'Unable to connect to the server. Please check your internet connection.';
      }
      return 'Network error occurred. Please try again.';
    }

    // HTTP status code errors
    const status = axiosError.response.status;
    const responseData = axiosError.response.data;

    // Extract error message from response
    // Handle both string responses and object responses
    let apiMessage: string | undefined;
    if (typeof responseData === 'string') {
      apiMessage = responseData;
    } else if (responseData && typeof responseData === 'object') {
      apiMessage = responseData.detail ?? responseData.message ?? responseData.error;
    }

    switch (status) {
      case 400:
        return apiMessage ?? 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return apiMessage ?? 'The requested resource was not found.';
      case 409:
        return apiMessage ?? 'Conflict occurred. Please check your request.';
      case 422:
        return apiMessage ?? 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'Server error occurred. Please try again later.';
      default:
        return apiMessage ?? defaultMessage;
    }
  }

  // Non-Axios errors
  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
}

/**
 * Fetch all Snowflake accounts for the authenticated user
 */
export async function fetchAccounts(token: string): Promise<ISnowflakeAccount[]> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/account`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    const response = await axios.get<ISnowflakeAccount[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000, // 30 second timeout
    });

    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error, 'Failed to load Snowflake accounts. Please try again.');
    console.error('Error fetching Snowflake accounts:', error);
    throw new Error(errorMessage);
  }
}

/**
 * Create a new Snowflake account with managed key authentication
 */
export async function createManagedKeyAccount(
  token: string,
  accountIdentifier: string,
  username: string,
  warehouse: string,
  role: string,
): Promise<{ account: ISnowflakeAccount; managedKeyData: IManagedKeyData }> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/account`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    if (!accountIdentifier) {
      throw new Error('Account identifier is required.');
    }

    const response = await axios.post<ICreateManagedKeyResponse>(
      url,
      {
        type: 'managed_pkey',
        account: accountIdentifier,
        username,
        warehouse,
        role,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      },
    );

    const accountData: ISnowflakeAccount = {
      id: response.data.account_id,
      account: accountIdentifier,
      username,
      warehouse,
      role,
      type: 'managed_pkey',
      is_editable: true,
      public_key_fingerprint: response.data.public_key_fingerprint,
    };

    const managedKeyData: IManagedKeyData = {
      publicKey: response.data.public_key,
      accountId: response.data.account_id,
      status: response.data.status,
      instructions: response.data.instructions,
    };

    return { account: accountData, managedKeyData };
  } catch (error) {
    const errorMessage = getErrorMessage(error, 'Failed to create Snowflake account. Please try again.');
    console.error('Error creating managed key account:', error);
    throw new Error(errorMessage);
  }
}

/**
 * Get details for a specific managed key account
 */
export async function getAccountDetails(
  token: string,
  accountId: string,
): Promise<{ account: ISnowflakeAccount; managedKeyData: IManagedKeyData }> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/managed-key-account/${accountId}`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    if (!accountId) {
      throw new Error('Account ID is required.');
    }

    const response = await axios.get<{
      account_id: number;
      account: string;
      username: string;
      warehouse: string;
      role: string;
      public_key: string;
      public_key_fingerprint: string;
    }>(url, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });

    const accountData: ISnowflakeAccount = {
      id: response.data.account_id,
      account: response.data.account,
      username: response.data.username,
      warehouse: response.data.warehouse,
      role: response.data.role,
      type: 'managed_pkey',
      is_editable: true,
      public_key_fingerprint: response.data.public_key_fingerprint,
    };

    const managedKeyData: IManagedKeyData = {
      publicKey: response.data.public_key,
      accountId: response.data.account_id,
      status: 'active',
      instructions: '',
    };

    return { account: accountData, managedKeyData };
  } catch (error) {
    const errorMessage = getErrorMessage(error, 'Failed to load account details. Please try again.');
    console.error('Error fetching account details:', error);
    throw new Error(errorMessage);
  }
}

/**
 * Delete a Snowflake account
 */
export async function deleteAccount(token: string, accountId: string): Promise<void> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/account/${accountId}`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    if (!accountId) {
      throw new Error('Account ID is required.');
    }

    await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });
  } catch (error) {
    const errorMessage = getErrorMessage(error, 'Failed to delete account. Please try again.');
    console.error('Error deleting account:', error);
    throw new Error(errorMessage);
  }
}

/**
 * Confirm and test Snowflake account connection
 */
export async function confirmConnection(token: string, accountId: string): Promise<void> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/account/${accountId}/confirm`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    if (!accountId) {
      throw new Error('Account ID is required.');
    }

    const response = await axios.post<IConfirmManagedKeyResponse>(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000, // Longer timeout for connection testing
      },
    );
    // Check if the response indicates an error even with 200 status
    if (response.data.status === 'error') {
      const errorMessage = response.data.message ?? 'Connection test failed';
      throw new Error(errorMessage);
    }
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      'Failed to verify connection. Please ensure you have run the SQL commands in Snowflake and try again.',
    );
    console.error('Error confirming connection:', error);
    throw new Error(errorMessage);
  }
}

/**
 * Update a managed key account (username, warehouse, role)
 */
export async function updateManagedKeyAccount(
  token: string,
  accountId: string,
  username: string,
  warehouse: string,
  role: string,
): Promise<void> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/account/${accountId}`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    if (!accountId) {
      throw new Error('Account ID is required.');
    }

    await axios.put(
      url,
      {
        type: 'managed_pkey',
        username,
        warehouse,
        role,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      },
    );
  } catch (error) {
    const errorMessage = getErrorMessage(error, 'Failed to update Snowflake account. Please try again.');
    console.error('Error updating managed key account:', error);
    throw new Error(errorMessage);
  }
}

/**
 * Validate a Snowflake account identifier
 */
export async function validateAccountIdentifier(token: string, identifier: string): Promise<IAccountValidationResult> {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/snowflake/validate-account/${encodeURIComponent(identifier)}`;

  try {
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    if (!identifier) {
      throw new Error('Account identifier is required.');
    }

    const response = await axios.get<{
      valid: boolean;
      message: string;
      account_identifier: string;
    }>(url, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });

    return {
      isValid: response.data.valid,
      message: response.data.message,
    };
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      'Failed to validate account identifier. Please check the format and try again.',
    );
    console.error('Error validating account identifier:', error);
    throw new Error(errorMessage);
  }
}
